import type { PromoKind, PropsView } from "@/lib/odds-screen/live/types";
import { slipScope } from "./book-links";
import { promoWithBookTerms } from "./promo-terms";
import { slipMultiplier } from "./rows";
import { ladderEv, priceLadder, targetForLeague, topPayout, type SlipTarget } from "./slip";

export type CandidatePromo = { readonly kind: PromoKind; readonly minPicks: number; readonly cap: number | null };

export type SlipCandidate = {
  readonly rowId: string;
  readonly eventId: string;
  readonly league: string;
  readonly scope: string | null;
  readonly entityKey: string;
  readonly p: number;
  readonly startMs: number;
  readonly promo: CandidatePromo | null;
  readonly multiplier: number;
};

export type GeneratedSlip = {
  readonly id: string;
  readonly targetId: string;
  readonly league: string | null;
  readonly rowIds: readonly string[];
  readonly hitAllPct: number;
  readonly evPct: number;
  readonly payout: number;
  readonly cap: number | null;
  readonly firstStartMs: number;
};

export type TargetSlips = {
  readonly target: SlipTarget;
  readonly slips: readonly GeneratedSlip[];
  readonly status: "ready" | "below_floor" | "short";
};

export type BestSlips = {
  readonly targets: readonly TargetSlips[];
  readonly best: GeneratedSlip | null;
  readonly unpriced: readonly string[];
};

export type EnteredSlip = {
  readonly id: string;
  readonly targetId: string;
  readonly rowIds: readonly string[];
  readonly firstStartMs: number;
  readonly enteredAtMs: number;
};

export const ENTERED_STORAGE_KEY = "fantasy_props_entered_v1";

export function slipId(targetId: string, rowIds: readonly string[]): string {
  return `${targetId}:${[...rowIds].sort().join(",")}`;
}

export type CandidateSource = { readonly ids: readonly string[]; readonly hitOf: (id: string) => number | undefined };

export function candidatesFrom(source: CandidateSource, view: PropsView, nowMs: number, book: string, standard: number | null): SlipCandidate[] {
  const out: SlipCandidate[] = [];
  for (const id of source.ids) {
    const row = view.rows.get(id);
    const event = row === undefined ? undefined : view.events.get(row.eventId);
    const hit = source.hitOf(id);
    if (row === undefined || event === undefined || hit === undefined || event.startMs <= nowMs) continue;
    out.push({
      rowId: id,
      eventId: event.id,
      league: row.league.toUpperCase(),
      scope: slipScope(book, row.links[0] || null, row.league.toUpperCase()),
      entityKey: row.entity.key,
      p: Math.min(0.999, Math.max(0.001, hit / 100)),
      startMs: event.startMs,
      promo: promoWithBookTerms(book, row.promo),
      multiplier: slipMultiplier(book, row.odds, standard),
    });
  }
  return out.sort((a, b) => b.p - a.p || (a.rowId < b.rowId ? -1 : a.rowId > b.rowId ? 1 : 0));
}

function pickLegs(pool: readonly SlipCandidate[], legs: number, taken: ReadonlySet<string>): SlipCandidate[] | null {
  const events = new Set<string>();
  const players = new Set<string>();
  const out: SlipCandidate[] = [];
  let promo = false;
  for (const candidate of pool) {
    if (taken.has(candidate.rowId) || events.has(candidate.eventId) || players.has(candidate.entityKey)) continue;
    if (candidate.promo !== null && (promo || candidate.promo.minPicks > legs)) continue;
    out.push(candidate);
    events.add(candidate.eventId);
    players.add(candidate.entityKey);
    promo ||= candidate.promo !== null;
    if (out.length === legs) return out;
  }
  return null;
}

function priceSlip(target: SlipTarget, legs: readonly SlipCandidate[], league: string | null): GeneratedSlip {
  const probs = legs.map((leg) => leg.p);
  const ladder = priceLadder(target, { multiplier: legs.reduce((acc, leg) => acc * leg.multiplier, 1), factor: 1, promo: legs.some((leg) => leg.promo !== null) });
  return {
    id: slipId(target.id, legs.map((leg) => leg.rowId)),
    targetId: target.id,
    league,
    rowIds: legs.map((leg) => leg.rowId),
    hitAllPct: probs.reduce((acc, p) => acc * p, 1) * 100,
    evPct: ladderEv(probs, ladder) * 100,
    payout: topPayout(ladder),
    cap: legs.find((leg) => leg.promo !== null)?.promo?.cap ?? null,
    firstStartMs: Math.min(...legs.map((leg) => leg.startMs)),
  };
}

function leaguePools(candidates: readonly SlipCandidate[]): { readonly league: string; readonly pool: SlipCandidate[] }[] {
  const pools = new Map<string, SlipCandidate[]>();
  for (const candidate of candidates) {
    const scope = candidate.scope ?? candidate.league;
    const pool = pools.get(scope);
    if (pool === undefined) pools.set(scope, [candidate]);
    else pool.push(candidate);
  }
  return [...pools].map(([, pool]) => ({ league: pool[0].league, pool }));
}

function bestSlipAcross(pools: readonly { readonly league: string | null; readonly pool: readonly SlipCandidate[] }[], target: SlipTarget, taken: ReadonlySet<string>): { legs: SlipCandidate[]; slip: GeneratedSlip } | null {
  let best: { legs: SlipCandidate[]; slip: GeneratedSlip } | null = null;
  for (const { league, pool } of pools) {
    const scoped = targetForLeague(target, league);
    if (scoped === null) continue;
    const legs = pickLegs(pool, scoped.legs, taken);
    if (legs === null) continue;
    const slip = priceSlip(scoped, legs, league);
    if (best === null || slip.evPct > best.slip.evPct) best = { legs, slip };
  }
  return best;
}

function statusRank(status: TargetSlips["status"]): number {
  return status === "ready" ? 0 : status === "below_floor" ? 1 : 2;
}

export function generateBestSlips({
  candidates, targets, unpriced, minEvPct, excluded, oneLeague, variants = 4,
}: {
  readonly candidates: readonly SlipCandidate[];
  readonly targets: readonly SlipTarget[];
  readonly unpriced: readonly string[];
  readonly minEvPct: number;
  readonly excluded: ReadonlySet<string>;
  readonly oneLeague: boolean;
  readonly variants?: number;
}): BestSlips {
  const pools = oneLeague ? leaguePools(candidates) : [{ league: null, pool: candidates }];
  const results: TargetSlips[] = targets.map((target) => {
    const taken = new Set(excluded);
    const slips: GeneratedSlip[] = [];
    let first: GeneratedSlip | null = null;
    while (slips.length < variants) {
      const found = bestSlipAcross(pools, target, taken);
      if (found === null) break;
      const { legs, slip } = found;
      first ??= slip;
      if (slip.evPct < minEvPct) break;
      slips.push(slip);
      for (const leg of legs) taken.add(leg.rowId);
    }
    if (slips.length > 0) return { target, slips, status: "ready" };
    if (first !== null) return { target, slips: [first], status: "below_floor" };
    return { target, slips: [], status: "short" };
  });
  results.sort((a, b) => statusRank(a.status) - statusRank(b.status) || (b.slips[0]?.evPct ?? -Infinity) - (a.slips[0]?.evPct ?? -Infinity));
  const lead = results[0];
  return { targets: results, best: lead !== undefined && lead.status === "ready" ? lead.slips[0] : null, unpriced };
}

export function liveEntered(entered: readonly EnteredSlip[], nowMs: number): EnteredSlip[] {
  return entered.filter((slip) => slip.firstStartMs > nowMs);
}

export function enteredRowIds(entered: readonly EnteredSlip[]): Set<string> {
  const out = new Set<string>();
  for (const slip of entered) for (const rowId of slip.rowIds) out.add(rowId);
  return out;
}

export function coerceStoredEntered(raw: unknown): EnteredSlip[] {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return [];
  const value = raw as { v?: unknown; slips?: unknown };
  if (value.v !== 1 || !Array.isArray(value.slips)) return [];
  const seen = new Set<string>();
  const out: EnteredSlip[] = [];
  for (const entry of value.slips) {
    if (!entry || typeof entry !== "object") continue;
    const slip = entry as Record<string, unknown>;
    if (typeof slip.id !== "string" || slip.id === "" || seen.has(slip.id) || typeof slip.targetId !== "string") continue;
    if (!Array.isArray(slip.rowIds) || slip.rowIds.some((id) => typeof id !== "string") || typeof slip.firstStartMs !== "number") continue;
    seen.add(slip.id);
    out.push({
      id: slip.id,
      targetId: slip.targetId,
      rowIds: slip.rowIds as string[],
      firstStartMs: slip.firstStartMs,
      enteredAtMs: typeof slip.enteredAtMs === "number" ? slip.enteredAtMs : 0,
    });
  }
  return out;
}
